import React, { useEffect, useState, useRef } from "react";
import {
  TextField,
  Button,
  CircularProgress,
  Box,
  Typography,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemText,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import HistoryIcon from "@mui/icons-material/History";
import ReplayIcon from "@mui/icons-material/Replay";
import {
  assistantChatApi,
  assistantConfirmApi,
  assistantGeneralApi,
  assistantConversationsApi,
  assistantConversationApi,
} from "../../../helpers/fakebackend_helper";

// Relative time like "just now", "2m ago", "3h ago"; older than a day shows the date + time.
const formatTimestamp = (ts, now) => {
  if (!ts) return "";
  const diff = Math.max(0, now - ts);
  const sec = Math.floor(diff / 1000);
  if (sec < 45) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return new Date(ts).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// MySQL "YYYY-MM-DD HH:MM:SS" -> epoch ms (treat as local time).
const parseDbTime = (s) => {
  if (!s) return Date.now();
  const ms = Date.parse(String(s).replace(" ", "T"));
  return Number.isNaN(ms) ? Date.now() : ms;
};

// --- Persistence: keep both transcripts across close/reopen and page reload. ---
// Keyed per logged-in user so two people on one browser never see each other's chat.
const STORAGE_PREFIX = "crmit_assistant_chat_v1_";
const MAX_KEPT = 200; // cap each transcript so localStorage cannot grow unbounded

const storageKey = () => {
  let userId = "anon";
  try {
    userId =
      JSON.parse(localStorage.getItem("authUser"))?.get_user_data?.User_Id ||
      "anon";
  } catch (_) {
    /* ignore */
  }
  return STORAGE_PREFIX + userId;
};

const loadPersisted = () => {
  try {
    return JSON.parse(localStorage.getItem(storageKey())) || {};
  } catch (_) {
    return {};
  }
};

/**
 * CRMit AI Assistant chat surface.
 *
 * Two independent conversations, switched by the mode toggle:
 *   business  -> agentic CRM assistant (POST /assistant/chat + /assistant/confirm).
 *   general   -> plain ChatGPT (POST /assistant/general), for questions outside the CRM.
 *
 * Every turn (both modes) is logged server-side and can be reloaded via the
 * history picker. localStorage keeps the last session for instant paint; the DB
 * is the source of truth so history follows the user across devices.
 */
const ChatCRMit = () => {
  const inputRef = useRef();
  const listRef = useRef(null);

  // Lazy-load any persisted conversation on first mount (instant paint).
  const [businessLogs, setBusinessLogs] = useState(() => loadPersisted().businessLogs || []);
  const [generalLogs, setGeneralLogs] = useState(() => loadPersisted().generalLogs || []);
  const [businessKey, setBusinessKey] = useState(() => loadPersisted().businessKey || "");
  const [generalKey, setGeneralKey] = useState(() => loadPersisted().generalKey || "");
  const [pending, setPending] = useState(() => loadPersisted().pending || null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("business");
  const [now, setNow] = useState(() => Date.now());
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [historyAnchor, setHistoryAnchor] = useState(null);

  const chatLogs = mode === "business" ? businessLogs : generalLogs;
  const setLogsFor = (m) => (m === "business" ? setBusinessLogs : setGeneralLogs);
  const setKeyFor = (m) => (m === "business" ? setBusinessKey : setGeneralKey);
  const appendLog = (targetMode, log) =>
    setLogsFor(targetMode)((prev) => [...prev, { time: Date.now(), ...log }]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  // Persist both transcripts (+ keys / pending) whenever they change.
  useEffect(() => {
    try {
      localStorage.setItem(
        storageKey(),
        JSON.stringify({
          businessLogs: businessLogs.slice(-MAX_KEPT),
          generalLogs: generalLogs.slice(-MAX_KEPT),
          businessKey,
          generalKey,
          pending,
        })
      );
    } catch (_) {
      /* quota / serialisation errors are non-fatal */
    }
  }, [businessLogs, generalLogs, businessKey, generalKey, pending]);

  // Load a stored conversation from the DB into its mode's transcript.
  const openConversation = async (key, targetMode, switchTo) => {
    const res = await assistantConversationApi(key);
    if (!res?.messages) return;
    const logs = res.messages.map((m) => ({
      sender: m.role === "user" ? "user" : "bot",
      text: m.text,
      time: parseDbTime(m.time),
    }));
    setLogsFor(targetMode)(logs);
    setKeyFor(targetMode)(key);
    if (targetMode === "business") setPending(null);
    if (switchTo) setMode(targetMode);
  };

  // On mount, pull the conversation list; for any mode with no local history,
  // rehydrate the most recent conversation from the DB (cross-device history).
  useEffect(() => {
    const hadBusiness = businessLogs.length > 0;
    const hadGeneral = generalLogs.length > 0;
    (async () => {
      const res = await assistantConversationsApi();
      const list = res?.conversations || [];
      setConversations(list);
      if (!hadBusiness) {
        const c = list.find((x) => x.Mode === "business");
        if (c) openConversation(c.ConversationKey, "business", false);
      }
      if (!hadGeneral) {
        const c = list.find((x) => x.Mode === "general");
        if (c) openConversation(c.ConversationKey, "general", false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToBottom = () => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };
  useEffect(scrollToBottom, [businessLogs, generalLogs, pending, loading, mode]);

  const handleCopy = (text, index) => {
    const done = () => {
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 1500);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(done);
    } else {
      done();
    }
  };

  const handleModeChange = (_e, next) => {
    if (!next || next === mode) return;
    setMode(next);
  };

  const handleClear = () => {
    if (mode === "business") {
      setBusinessLogs([]);
      setBusinessKey("");
      setPending(null);
    } else {
      setGeneralLogs([]);
      setGeneralKey("");
    }
  };

  const openHistory = async (e) => {
    setHistoryAnchor(e.currentTarget);
    const res = await assistantConversationsApi(); // refresh the list on open
    if (res?.conversations) setConversations(res.conversations);
  };
  const pickHistory = (c) => {
    setHistoryAnchor(null);
    openConversation(c.ConversationKey, c.Mode, true);
  };

  // Fold a backend response into the given mode's transcript (+ business pending state).
  // `attempt` describes what was sent so a failed turn can be retried.
  const applyResponse = (res, targetMode, attempt) => {
    // Detect failure across all the shapes the backend can return: a thrown
    // network/parse error ({status:0,error}), a framework exception (CI4 uses
    // `code`, not `status`, e.g. a 500), and our own non-200 JSON responses.
    const errText = !res
      ? "No response from the server. It may be offline. Please retry."
      : res.error
      ? res.error
      : res.code && Number(res.code) >= 400
      ? res.message || res.messages?.error || `Request failed (${res.code}).`
      : res.status && res.status !== 200
      ? res.message || res.response || `Request failed (${res.status}).`
      : null;

    if (errText) {
      appendLog(targetMode, {
        sender: "bot",
        error: true,
        text: errText,
        retry: attempt || null,
      });
      return;
    }
    if (res.ConversationKey) setKeyFor(targetMode)(res.ConversationKey);

    if (res.response) appendLog(targetMode, { sender: "bot", text: res.response });

    if (res.executed_action?.url) {
      appendLog(targetMode, {
        sender: "bot",
        link: res.executed_action.url,
        text: "Open it",
      });
    }

    if (res.type === "pending" && res.pending_action) {
      const pa = res.pending_action;
      appendLog(targetMode, {
        sender: "bot",
        pendingId: pa.Id,
        pendingPrompt: pa.ConfirmPrompt || "Confirm this action?",
      });
      setPending(pa);
    } else {
      setPending(null);
    }
  };

  const handleSend = async () => {
    const message = inputRef.current?.value.trim();
    if (!message || loading) return;

    const sendMode = mode; // capture: the toggle may change while awaiting the reply
    const key = sendMode === "business" ? businessKey : generalKey;
    appendLog(sendMode, { sender: "user", text: message });
    inputRef.current.value = "";
    setLoading(true);
    try {
      const payload = { ...(key ? { ConversationKey: key } : {}), Message: message };
      const res =
        sendMode === "general"
          ? await assistantGeneralApi(payload)
          : await assistantChatApi(payload);
      applyResponse(res, sendMode, { kind: "send", mode: sendMode, message });
    } finally {
      setLoading(false);
    }
  };

  // Re-run a failed turn from its stored attempt descriptor, dropping the error bubble.
  const handleRetry = async (log) => {
    const attempt = log.retry;
    if (!attempt || loading) return;
    setLogsFor(attempt.mode)((prev) => prev.filter((l) => l !== log));
    setLoading(true);
    try {
      let res;
      if (attempt.kind === "decision") {
        res = await assistantConfirmApi({
          ConversationKey: businessKey,
          PendingActionId: attempt.pendingId,
          Decision: attempt.decision,
        });
      } else {
        const key = attempt.mode === "business" ? businessKey : generalKey;
        const payload = { ...(key ? { ConversationKey: key } : {}), Message: attempt.message };
        res =
          attempt.mode === "general"
            ? await assistantGeneralApi(payload)
            : await assistantChatApi(payload);
      }
      applyResponse(res, attempt.mode, attempt);
    } finally {
      setLoading(false);
    }
  };

  const handleDecision = async (decision) => {
    if (!pending || loading) return;
    const current = pending;
    setPending(null);
    setLoading(true);
    appendLog("business", {
      sender: "user",
      text: decision === "allow" ? "✓ Approved" : "✗ Declined",
    });
    try {
      const res = await assistantConfirmApi({
        ConversationKey: businessKey,
        PendingActionId: current.Id,
        Decision: decision,
      });
      applyResponse(res, "business", {
        kind: "decision",
        mode: "business",
        decision,
        pendingId: current.Id,
      });
    } finally {
      setLoading(false);
    }
  };

  const timeCaption = (log, align) => (
    <Typography
      variant="caption"
      sx={{ color: "#9e9e9e", fontSize: "0.62rem", mt: 0.25, px: 0.5, textAlign: align }}
    >
      {formatTimestamp(log.time, now)}
    </Typography>
  );

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        backgroundColor: "#fff",
      }}
    >
      {/* Mode toggle + history/clear (pinned; never scrolls away) */}
      <Box
        sx={{
          flexShrink: 0,
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 1,
          borderBottom: "1px solid #eee",
          backgroundColor: "#fafafa",
        }}
      >
        <Tooltip title="Conversation history" arrow>
          <IconButton
            size="small"
            onClick={openHistory}
            sx={{ position: "absolute", left: 6, color: "#9e9e9e" }}
          >
            <HistoryIcon sx={{ fontSize: "1.1rem" }} />
          </IconButton>
        </Tooltip>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={mode}
          onChange={handleModeChange}
          sx={{ "& .MuiToggleButton-root": { fontSize: "0.72rem", py: 0.25, px: 1.5 } }}
        >
          <ToggleButton value="business">Business</ToggleButton>
          <ToggleButton value="general">ChatGPT</ToggleButton>
        </ToggleButtonGroup>

        {chatLogs.length > 0 && (
          <Tooltip title="New / clear this conversation" arrow>
            <IconButton
              size="small"
              onClick={handleClear}
              disabled={loading}
              sx={{ position: "absolute", right: 6, color: "#9e9e9e" }}
            >
              <DeleteOutlineIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Menu
        anchorEl={historyAnchor}
        open={Boolean(historyAnchor)}
        onClose={() => setHistoryAnchor(null)}
        PaperProps={{ sx: { maxHeight: 320, width: 280 } }}
      >
        {conversations.length === 0 && (
          <MenuItem disabled>
            <ListItemText primary="No saved conversations yet" />
          </MenuItem>
        )}
        {conversations.map((c) => (
          <MenuItem key={c.ConversationKey} onClick={() => pickHistory(c)}>
            <ListItemText
              primary={c.Title || "Untitled"}
              primaryTypographyProps={{ noWrap: true, fontSize: "0.8rem" }}
              secondary={`${c.Mode === "general" ? "ChatGPT" : "Business"} · ${formatTimestamp(
                parseDbTime(c.UpdatedDateTime || c.CreatedDateTime),
                now
              )}`}
              secondaryTypographyProps={{ fontSize: "0.65rem" }}
            />
          </MenuItem>
        ))}
      </Menu>

      {/* Scrollable transcript (per-mode) */}
      <Box
        ref={listRef}
        sx={{
          flex: 1,
          minHeight: 0,
          padding: "16px",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {chatLogs.length === 0 && (
          <Typography variant="body2" sx={{ color: "#888", textAlign: "center", mt: 2, fontSize: "0.8rem" }}>
            {mode === "business"
              ? 'Ask me to find something or do something in your CRM, e.g. "what were my sales this month?" or "raise a task to make coffee tomorrow at 9am".'
              : "General ChatGPT mode. Ask me anything (coding, error messages, explanations). I can't see your CRM data in this mode."}
          </Typography>
        )}

        {chatLogs.map((log, index) => {
          if (log.pendingPrompt) {
            const active = pending && pending.Id === log.pendingId;
            return (
              <Box
                key={index}
                sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", mb: 1 }}
              >
                <Box
                  sx={{
                    maxWidth: "90%",
                    p: 1.25,
                    borderRadius: "8px",
                    border: "1px solid #ffb74d",
                    backgroundColor: "#fff8e1",
                  }}
                >
                  <Typography sx={{ mb: active ? 1 : 0, fontWeight: 600, fontSize: "0.8rem" }}>
                    {log.pendingPrompt}
                  </Typography>
                  {active && (
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        disabled={loading}
                        onClick={() => handleDecision("allow")}
                      >
                        Approve
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="inherit"
                        disabled={loading}
                        onClick={() => handleDecision("deny")}
                      >
                        Deny
                      </Button>
                    </Stack>
                  )}
                </Box>
                {timeCaption(log, "left")}
              </Box>
            );
          }

          const isUser = log.sender === "user";
          return (
            <Box
              key={index}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: isUser ? "flex-end" : "flex-start",
                mb: 1,
              }}
            >
              {log.link ? (
                <Typography
                  component="a"
                  href={log.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    padding: "6px 10px",
                    borderRadius: "8px",
                    fontSize: "0.8rem",
                    backgroundColor: "#e3f2fd",
                    color: "#1565c0",
                    textDecoration: "underline",
                    maxWidth: "80%",
                  }}
                >
                  {log.text}
                </Typography>
              ) : (
                <Typography
                  sx={{
                    padding: "6px 10px",
                    borderRadius: "8px",
                    fontSize: "0.8rem",
                    lineHeight: 1.45,
                    whiteSpace: "pre-wrap",
                    backgroundColor: isUser ? "#1976d2" : log.error ? "#d32f2f" : "#f5f5f5",
                    color: isUser ? "#fff" : log.error ? "#fff" : "#000",
                    maxWidth: "80%",
                  }}
                >
                  {log.text}
                </Typography>
              )}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: isUser ? "row-reverse" : "row",
                  alignItems: "center",
                  gap: 0.25,
                }}
              >
                {timeCaption(log, isUser ? "right" : "left")}
                {log.error && log.retry && (
                  <Button
                    size="small"
                    variant="text"
                    color="error"
                    disabled={loading}
                    onClick={() => handleRetry(log)}
                    startIcon={<ReplayIcon sx={{ fontSize: "0.8rem" }} />}
                    sx={{ fontSize: "0.62rem", minWidth: 0, p: "0 4px", "& .MuiButton-startIcon": { mr: 0.25 } }}
                  >
                    Retry
                  </Button>
                )}
                {!isUser && !log.error && !log.link && log.text && (
                  <Tooltip title={copiedIndex === index ? "Copied" : "Copy"} arrow>
                    <IconButton
                      size="small"
                      onClick={() => handleCopy(log.text, index)}
                      sx={{ p: 0.25, color: "#9e9e9e" }}
                    >
                      {copiedIndex === index ? (
                        <CheckIcon sx={{ fontSize: "0.85rem", color: "#4caf50" }} />
                      ) : (
                        <ContentCopyIcon sx={{ fontSize: "0.85rem" }} />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          );
        })}

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
            <CircularProgress size={20} />
          </Box>
        )}
      </Box>

      {/* Input */}
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          gap: 1,
          padding: "8px",
          borderTop: "1px solid #ccc",
          backgroundColor: "#f1f1f1",
        }}
      >
        <TextField
          inputRef={inputRef}
          fullWidth
          size="small"
          variant="outlined"
          placeholder={
            mode === "business" ? "Ask about your CRM..." : "Ask ChatGPT anything..."
          }
          inputProps={{ autoComplete: "off", style: { fontSize: "0.8rem" } }}
          onKeyPress={(e) => {
            if (e.key === "Enter") handleSend();
          }}
        />
        <Button variant="contained" color="primary" onClick={handleSend} disabled={loading}>
          Send
        </Button>
      </Box>
    </Box>
  );
};

export default ChatCRMit;
